import { Note } from "../../music/note";
import type { Piano } from "../piano";
import type { PianoInput } from "./input";

/** Best-effort heuristic on device names (no first-class transport field on Web MIDI). */
export type MidiDeviceTransport = "usb" | "bluetooth" | "virtual";

export interface MidiDeviceInfo {
  id: string;
  name: string;
  manufacturer: string;
  transport: MidiDeviceTransport;
  /** `true` while the device is currently connected. */
  connected: boolean;
  /** `true` when this device is currently routed to the piano (toggle in settings). */
  enabled: boolean;
}

export type MidiInputChangeListener = () => void;
export type MidiActivityListener = (event: { id: string; midi: number; velocity: number }) => void;

/**
 * Common surface implemented by both `WebMidiInput` (browser) and
 * `NativeMidiInput` (Capacitor iOS plugin). The studio context and settings
 * overlay only depend on this interface.
 */
export interface MidiInputAdapter extends PianoInput {
  /** Resolves once the input is live, rejects when the platform denies access. */
  attach(): Promise<void>;
  getDevices(): readonly MidiDeviceInfo[];
  getActiveDeviceId(): string | null;
  setActiveDevice(id: string | null): void;
  setEnabled(id: string, enabled: boolean): void;
  rescan(): void;
  onChange(listener: MidiInputChangeListener): () => void;
  onActivity(listener: MidiActivityListener): () => void;
}

export function detectTransport(name: string | null): MidiDeviceTransport {
  const n = (name ?? "").toLowerCase();

  if (/bluetooth|\bbt\b/.test(n)) {
    return "bluetooth";
  }

  if (/iac|virtual|loopmidi/.test(n)) {
    return "virtual";
  }

  return "usb";
}

/** Plus vieux que ça, un horodatage ne décrit plus un geste en cours. */
const MAX_GESTURE_AGE_MS = 1000;

/**
 * Tolérance sur un horodatage qui devance `performance.now()` d'un cheveu :
 * deux lectures de la même horloge, à deux instants.
 */
const FUTURE_SLACK_MS = 2;

/**
 * L'instant d'un message MIDI sur l'horloge de position, en secondes.
 *
 * `MIDIMessageEvent.timeStamp` est un `DOMHighResTimeStamp` : la même échelle
 * que `performance.now()`, donc que `PerformanceClock`. Il date la réception
 * du message par le navigateur, pas l'exécution du gestionnaire — l'écart
 * entre les deux est l'attente du fil principal, une image au pire quand le
 * roll rend, et c'est exactement ce qu'on veut retirer du tick. Le son, lui,
 * ne remonte pas le temps ; mais la note se pose sur la grille là où elle a
 * été jouée, chez soi comme chez le partenaire, et le trajet mesuré inclut
 * alors cette attente, qui est réelle.
 *
 * On ne le croit que s'il est plausible : fini, pas dans le futur, et pas plus
 * vieux qu'une seconde. Un zéro — une plateforme qui ne renseigne pas — ou une
 * valeur absurde daterait le geste n'importe où sur la grille ; on retombe
 * alors sur maintenant, comme avant.
 */
export function gestureInstant(
  timeStamp: number,
  nowMs: number = performance.now(),
): number | undefined {
  if (!Number.isFinite(timeStamp) || timeStamp <= 0) {
    return undefined;
  }

  if (timeStamp > nowMs + FUTURE_SLACK_MS || nowMs - timeStamp > MAX_GESTURE_AGE_MS) {
    return undefined;
  }

  return Math.min(timeStamp, nowMs) / 1000;
}

/**
 * Dispatches a parsed MIDI message into the piano. Returns `true` when the
 * message produced a note-on (so the caller can emit an activity event).
 *
 * `at` est l'instant du message sur l'horloge de position, en secondes, quand
 * la plateforme l'horodate ; voir `gestureInstant`.
 */
export function dispatchMidiMessage(
  piano: Piano,
  data: Uint8Array | number[],
  at?: number,
): { kind: "noteOn"; note: Note; midi: number; velocity: number } | null {
  if (data.length === 0) {
    return null;
  }

  const status = data[0];

  if (status === 248 || status === 254) {
    return null;
  }

  const cmd = status & 0xf0;
  const noteNum = data[1] ?? 0;
  const velocity = data[2] ?? 0;

  try {
    const note = Note.fromMidi(noteNum);

    if (cmd === 0x90 && velocity > 0) {
      // La vélocité telle que le clavier l'a envoyée, sans courbe. L'ancienne
      // étirait 25–76 (sur 127) jusqu'à 1 : au-delà de 76, tout valait 1, et
      // mf, f et ff sonnaient, partaient en séance et se dessinaient pareil.
      if (piano.contains(note)) {
        piano.press(note, velocity / 127, "player", at);

        return { kind: "noteOn", note, midi: noteNum, velocity };
      }

      return null;
    }

    if (cmd === 0x80 || (cmd === 0x90 && velocity === 0)) {
      piano.release(note, "player", at);

      return null;
    }

    if (cmd === 0xb0 && noteNum === 64) {
      const isDown = velocity >= 64;

      if (isDown) {
        piano.sustainOn("player", at);
      } else {
        piano.sustainOff("player", at);
      }
    }
  } catch {
    // Ignore unparseable note numbers.
  }

  return null;
}
